/**
 * Cart page — shows held slots, total, and a "Pay" button that creates a
 * Stripe Checkout session and redirects to Stripe.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useStudentAuth } from "../StudentAuthContext";

function fmtDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString([], {
    weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
}

function useCountdown(expiresAtIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAtIso) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [expiresAtIso]);
  if (!expiresAtIso) return null;
  const ms = new Date(expiresAtIso).getTime() - now;
  if (ms <= 0) return "expired";
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function Cart() {
  const { status, token } = useStudentAuth();
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total_gbp: 0, soonest_expiry: null });
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  const countdown = useCountdown(cart?.soonest_expiry);
  const isExpired = countdown === "expired";

  useEffect(() => {
    if (status === "anon") {
      navigate("/login", { state: { next: "/cart" }, replace: true });
      return;
    }
    if (status !== "auth") return;
    (async () => {
      try {
        const c = await api.cartGet(token);
        setCart(c);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [status, token, navigate]);

  async function remove(holdId) {
    setError("");
    try {
      const c = await api.cartRemove(token, holdId);
      setCart(c);
    } catch (err) {
      setError(err.message);
    }
  }

  async function pay() {
    setPaying(true);
    setError("");
    try {
      const { url } = await api.checkoutSession(token, window.location.origin);
      window.location.href = url;
    } catch (err) {
      setError(err.message || "Could not start payment");
      setPaying(false);
    }
  }

  const hasItems = (cart?.items || []).length > 0;

  return (
    <main className="min-h-[100dvh] bg-[#FAF9F6] pt-20 md:pt-24 pb-16 px-6 md:px-10" data-testid="cart-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-3">
          Cart
        </div>
        <h1 className="font-display text-4xl md:text-6xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
          Your lessons.
        </h1>

        {loading ? (
          <div className="mt-10 text-[#1C1C1C]/60">Loading your cart…</div>
        ) : !hasItems ? (
          <div className="mt-10 p-8 bg-white border border-[#1C1C1C]/10 rounded-2xl text-center" data-testid="cart-empty">
            <p className="text-[#1C1C1C]/70 mb-6">Your cart is empty.</p>
            <button
              onClick={() => navigate("/book")}
              className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85 transition-colors"
              data-testid="cart-find-slots-btn"
            >
              Find a lesson →
            </button>
          </div>
        ) : (
          <>
            {countdown && (
              <div
                className={`mt-6 text-[11px] tracking-[0.24em] uppercase ${isExpired ? "text-[#B83A3A]" : "text-[#1C1C1C]/55"}`}
                data-testid="cart-countdown"
              >
                {isExpired
                  ? "Your hold expired — please re-add the slots."
                  : `Slots held for ${countdown}`}
              </div>
            )}
            <ul className="mt-6 space-y-3" data-testid="cart-items">
              {cart.items.map((it) => (
                <li
                  key={it.hold_id}
                  data-testid={`cart-item-${it.hold_id}`}
                  className="flex items-center justify-between gap-4 bg-white border border-[#1C1C1C]/10 rounded-2xl p-5"
                >
                  <div>
                    <div className="font-display text-lg text-[#1C1C1C]">{it.tier_label}</div>
                    <div className="text-sm text-[#1C1C1C]/60 mt-1">{fmtDateTime(it.start)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl text-[#1C1C1C]">£{it.amount_gbp.toFixed(2)}</div>
                    <button
                      data-testid={`cart-remove-${it.hold_id}`}
                      onClick={() => remove(it.hold_id)}
                      className="mt-1 text-[10px] tracking-[0.24em] uppercase text-[#B83A3A]/80 hover:text-[#B83A3A]"
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-8 flex items-baseline justify-between border-t border-[#1C1C1C]/10 pt-6">
              <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60">
                Total
              </div>
              <div className="font-display text-4xl tracking-tighter text-[#1C1C1C]" data-testid="cart-total">
                £{cart.total_gbp.toFixed(2)}
              </div>
            </div>

            {error && (
              <div className="mt-4 text-sm text-[#B83A3A]" data-testid="cart-error">
                {error}
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                data-testid="cart-pay-btn"
                onClick={pay}
                disabled={paying || isExpired}
                className="px-6 py-4 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.2em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
              >
                {paying ? "Redirecting…" : "Pay securely →"}
              </button>
              <button
                onClick={() => navigate("/book")}
                className="px-6 py-4 bg-white border border-[#1C1C1C]/15 text-[#1C1C1C] rounded-xl text-sm tracking-[0.2em] uppercase font-medium hover:border-[#1C1C1C]/40 transition-colors"
                data-testid="cart-add-more-btn"
              >
                Add more
              </button>
            </div>
            <p className="mt-4 text-xs text-[#1C1C1C]/50">
              You'll be redirected to Stripe to complete the payment securely.
            </p>
          </>
        )}
      </div>
    </main>
  );
}
